import React, { useState, useRef, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Box, Divider, Grid } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import Page from "../../../components/Page";
import { makeStyles } from "tss-react/mui";
import Results from "./Results/Results";
import SourceDialog from "./components/SourceDialog";
import EntityDialog from "./components/EntityDialog";
import TabularDialog from "./components/TabularDialog";
import StreamMessage from "./components/StreamMessage";
import QueryBox from "./components/QueryBox/QueryBox";
import ScrollToBottom from "./components/ScrollToBottom";
import Loader from "../../../components/Loader";
import { getSessionMessages } from "../../../core/repo/sessionRepo";
import { groupMessagesByQuestion } from "./utils";
import { useLocation } from "react-router-dom";
import { useQueryHandler } from "../../../hooks/useQueryHandler";
import {
  setQuerySuccess,
  setQueryFiltersSuccess,
} from "../../../core/events/queryEvents";
import Header from "./Header";
import { AI_SEARCH_URL } from "../../../utils/routes";
import {
  CLICK_ENTITY,
  CLICK_SOURCE,
  CLICK_TABULAR,
} from "../../../utils/posthogEvents";
import { usePostHog } from "posthog-js/react";
import RelatedDocDialog from "./Results/Message/RelatedDocs/RelatedDocDialog";
import FileFromStreamDialog from "./components/FileFromStreamDialog/FileFromStreamDialog";
import PIDMarkup from "./components/PIDMarkup/PIDMarkup";

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
  },
}));

function AI_Search() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { classes } = useStyles();
  const { sessionId } = useParams();
  const { query } = useSelector((state) => state.query);
  const { selectedDomain } = useSelector((state) => state.domain);
  const [entity, setEntity] = useState(null);
  const [tabular, setTabular] = useState(null);
  const messagesRef = useRef();
  const messagesEndRef = useRef();
  const messageRefs = useRef({});
  const [messages, setMessages] = useState([]);
  const [areMessagesFetched, setAreMessagesFetched] = useState(false);
  const messageId = new URLSearchParams(location.search).get("messageId");
  const [isLoading, setIsLoading] = useState(false);
  const queryBoxContainerRef = useRef();
  const [queryContainerHeight, setQueryContainerHeight] = useState(0);
  const fromHistory = new URLSearchParams(location.search).get("fromHistory");
  const [selectedRelatedDoc, setSelectedRelatedDoc] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedFileFromStream, setSelectedFileFromStream] = useState(null);
  const [isolationMarkup, setIsolationMarkup] = useState(null);
  const [selectedIsolationMarkup, setSelectedIsolationMarkup] = useState(null);

  const isInitialMount = useRef(true);
  const posthog = usePostHog();
  const [lastStreamedMessageId, setLastStreamedMessageId] = useState(null);

  const handleMessageUpdate = (newMessage) => {
    setMessages((prev) => [...prev, newMessage]);
    setLastStreamedMessageId(newMessage.query["message_id"]);
  };

  const { handleQueryIntent, tempQandA, isMetadataLoading, relatedDocs } =
    useQueryHandler(handleMessageUpdate, sessionId);

  const handleQuery = (query, taggedFiles = [], taggedCategories = []) => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 10);
    handleQueryIntent(query, taggedFiles, taggedCategories);
    setSelectedSource(null);
    setSelectedRelatedDoc(null);
    setEntity(null);
    setTabular(null);
    setSelectedFileFromStream(null);
    setIsolationMarkup(null);
    setSelectedIsolationMarkup(null);
  };

  async function fetchMessages() {
    setIsLoading(true);
    try {
      const response = await dispatch(getSessionMessages(sessionId));
      const processedMessages = groupMessagesByQuestion(
        response["messages"] || []
      );
      setMessages(processedMessages);
    } catch (e) {
      console.log(e);
    }
    setIsLoading(false);
    setAreMessagesFetched(true);
  }

  useEffect(() => {
    fetchMessages();
    setEntity(null);
    setTabular(null);
    setSelectedRelatedDoc(null);
    setSelectedSource(null);
    setSelectedFileFromStream(null);
    setIsolationMarkup(null);
    setSelectedIsolationMarkup(null);
  }, [sessionId]);

  useEffect(() => {
    areMessagesFetched && handleScroll();
  }, [areMessagesFetched, messages.length]);

  useEffect(() => {
    if (query) {
      handleQuery(query);
    }
  }, [query]);

  useEffect(() => {
    return () => {
      dispatch(setQuerySuccess(""));
      dispatch(setQueryFiltersSuccess({ taggedFiles: [] }));
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedDomain && !isInitialMount.current) {
      history.push({
        pathname: AI_SEARCH_URL,
      });
    }
    isInitialMount.current = false;
  }, [selectedDomain]);

  //code to get the height of the query container which is used to decide the height of the last message
  useEffect(() => {
    const queryContainer = queryBoxContainerRef.current;

    if (queryContainer) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const newHeight = entry.contentRect.height;
          setQueryContainerHeight(newHeight);
        }
      });

      resizeObserver.observe(queryContainer);

      return () => {
        resizeObserver.unobserve(queryContainer);
      };
    }
  }, []);

  function handleScroll() {
    setTimeout(() => {
      if (messageId && messageRefs.current[messageId]) {
        messageRefs.current[messageId].scrollIntoView({
          behavior: "smooth",
        });
      } else if (messagesEndRef.current) {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }
    }, 10);
  }

  const handleEntityChange = (entity) => {
    setEntity(entity);
    setTabular(null);
    posthog.capture(CLICK_ENTITY);
    setSelectedRelatedDoc(null);
    setSelectedSource(null);
    setSelectedFileFromStream(null);
    setIsolationMarkup(null);
    setSelectedIsolationMarkup(null);
  };

  const handleTabularChange = (tabular) => {
    setTabular(tabular);
    setEntity(null);
    posthog.capture(CLICK_TABULAR);
    setSelectedRelatedDoc(null);
    setSelectedSource(null);
    setSelectedFileFromStream(null);
    setSelectedIsolationMarkup(null);
  };

  const handleSelectedRelatedDocChange = (relatedDoc) => {
    setSelectedRelatedDoc(relatedDoc);
    setEntity(null);
    setTabular(null);
    setSelectedSource(null);
    setSelectedFileFromStream(null);
    setSelectedIsolationMarkup(null);
  };

  const handleSelectedSourceChange = (source) => {
    setSelectedSource(source);
    setEntity(null);
    setTabular(null);
    setSelectedRelatedDoc(null);
    setSelectedFileFromStream(null);
    setIsolationMarkup(null);
    setSelectedIsolationMarkup(null);
  };

  const handleSelectedFileFromStreamChange = (file) => {
    setSelectedFileFromStream(file);
    setEntity(null);
    setTabular(null);
    setSelectedRelatedDoc(null);
    setSelectedSource(null);
    setIsolationMarkup(null);
    setSelectedIsolationMarkup(null);
  };

  const handleIsolationMarkupChange = (isolationMarkup) => {
    setIsolationMarkup(isolationMarkup);
    setEntity(null);
    setTabular(null);
    setSelectedRelatedDoc(null);
    setSelectedSource(null);
    setSelectedFileFromStream(null);
  };

  const handleSelectedIsolationMarkupChange = (isolationMarkup) => {
    setSelectedIsolationMarkup(isolationMarkup);
  };

  const handleCloseSourceDialog = () => {
    setSelectedSource(null);
  };

  const handleCloseRelatedDocDialog = () => {
    setSelectedRelatedDoc(null);
  };

  const handleCloseFileFromStreamDialog = () => {
    setSelectedFileFromStream(null);
  };

  const handleClosePIDMarkupDialog = () => {
    setIsolationMarkup(null);
    setSelectedIsolationMarkup(null);
    setLastStreamedMessageId(null);
  };

  const handleUpdateMessage = (message) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.query["message_id"] === message.query["message_id"] ? message : m
      )
    );
  };

  return (
    <Page className={classes.root} title={"Search"}>
      <Box
        display={"flex"}
        flexDirection={"column"}
        height={"100%"}
        position={"relative"}>
        <Header />
        <Divider />
        <Box flex={1} overflow={"auto"}>
          <Grid container height={"100%"}>
            <Grid
              item
              size={{
                md:
                  selectedSource || selectedRelatedDoc || isolationMarkup
                    ? 7
                    : entity || tabular || selectedFileFromStream
                    ? 4
                    : 12,
              }}
              height={"100%"}
              width={"100%"}>
              <Box
                display={"flex"}
                flexDirection={"column"}
                height={"100%"}
                position={"relative"}>
                <Box ref={messagesRef} flex={1} overflow={"auto"}>
                  <Box
                    px={{ xs: 2, sm: 3 }}
                    pb={{ xs: 4, sm: 6 }}
                    pt={{ xs: 2, sm: 3 }}
                    maxWidth={900}
                    margin={"0 auto"}>
                    <Results
                      isStreaming={tempQandA}
                      relatedDocsFromStreaming={relatedDocs}
                      messages={messages}
                      messageRefs={messageRefs}
                      handleEntityChange={handleEntityChange}
                      handleTabularChange={handleTabularChange}
                      queryContainerHeight={queryContainerHeight}
                      selectedSource={selectedSource}
                      setSelectedSource={handleSelectedSourceChange}
                      onMessageUpdate={handleUpdateMessage}
                      selectedRelatedDoc={selectedRelatedDoc}
                      setSelectedRelatedDoc={handleSelectedRelatedDocChange}
                      selectedFileFromStream={selectedFileFromStream}
                      onSelectedFileFromStreamChange={
                        handleSelectedFileFromStreamChange
                      }
                      isolationMarkup={isolationMarkup}
                      onIsolationMarkupChange={handleIsolationMarkupChange}
                      selectedIsolationMarkup={selectedIsolationMarkup}
                      onSelectedIsolationMarkupChange={
                        handleSelectedIsolationMarkupChange
                      }
                      lastStreamedMessageId={lastStreamedMessageId}
                    />
                    {tempQandA && (
                      <StreamMessage
                        queryContainerHeight={queryContainerHeight}
                        tempQandA={tempQandA}
                        isMetadataLoading={isMetadataLoading}
                        relatedDocs={relatedDocs}
                        setSelectedRelatedDoc={handleSelectedRelatedDocChange}
                        onIsolationMarkupChange={handleIsolationMarkupChange}
                        onSelectedIsolationMarkupChange={
                          handleSelectedIsolationMarkupChange
                        }
                      />
                    )}
                    <Box ref={messagesEndRef} />
                  </Box>
                </Box>
                <ScrollToBottom
                  messagesRef={messagesRef}
                  messagesEndRef={messagesEndRef}
                />
                <Box
                  width={"100%"}
                  maxWidth={900}
                  margin={"0 auto"}
                  px={{ xs: 2, sm: 3 }}
                  pb={{ xs: 1, sm: 2 }}>
                  <QueryBox
                    isStreaming={tempQandA}
                    handleQuery={handleQuery}
                    queryBoxContainerRef={queryBoxContainerRef}
                  />
                </Box>
              </Box>
            </Grid>
            {selectedSource && (
              <SourceDialog
                selectedSource={selectedSource}
                onClose={handleCloseSourceDialog}
              />
            )}
            {entity && (
              <EntityDialog
                entity={entity}
                handleEntityChange={handleEntityChange}
              />
            )}
            {tabular && (
              <TabularDialog
                fileId={tabular["file_id"]}
                source={tabular}
                handleSourceChange={handleTabularChange}
              />
            )}
            {selectedRelatedDoc && (
              <RelatedDocDialog
                relatedDoc={selectedRelatedDoc}
                onClose={handleCloseRelatedDocDialog}
              />
            )}
            {selectedFileFromStream && (
              <FileFromStreamDialog
                file={selectedFileFromStream}
                onClose={handleCloseFileFromStreamDialog}
              />
            )}
            {isolationMarkup && (
              <PIDMarkup
                isStreaming={tempQandA}
                isolationMarkup={isolationMarkup}
                onIsolationMarkupChange={handleIsolationMarkupChange}
                selectedIsolationMarkup={selectedIsolationMarkup}
                onSelectedIsolationMarkupChange={
                  handleSelectedIsolationMarkupChange
                }
                lastStreamedMessageId={lastStreamedMessageId}
                onClose={handleClosePIDMarkupDialog}
              />
            )}
          </Grid>
        </Box>
        {isLoading && <Loader />}
      </Box>
    </Page>
  );
}

export default AI_Search;
